/**
 * NewJobPopup.js - Real-time popup for incoming jobs
 * Shows workers new job opportunities with Accept/Schedule/Decline options
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MapPin, Clock, DollarSign, User, Phone, Calendar,
  Check, AlertCircle, Car, Package, ShoppingBag, Shirt, Briefcase,
  Navigation, Timer, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

// Service icons
const SERVICE_ICONS = {
  rides: Car,
  courier: Package,
  errands: ShoppingBag,
  laundry: Shirt,
  default: Briefcase
};

// Service colors
const SERVICE_COLORS = {
  rides: { bg: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/50', text: 'text-emerald-400' },
  courier: { bg: 'from-cyan-500/20 to-blue-500/20', border: 'border-cyan-500/50', text: 'text-cyan-400' },
  errands: { bg: 'from-pink-500/20 to-orange-500/20', border: 'border-pink-500/50', text: 'text-pink-400' },
  laundry: { bg: 'from-purple-500/20 to-violet-500/20', border: 'border-purple-500/50', text: 'text-purple-400' },
  default: { bg: 'from-gray-500/20 to-slate-500/20', border: 'border-gray-500/50', text: 'text-gray-400' }
};

const NewJobPopup = ({ job, onAccept, onSchedule, onDecline, onClose }) => {
  const [accepting, setAccepting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 second auto-dismiss
  const timerRef = useRef(null);
  
  const serviceType = job?.service_type || job?.type || 'default';
  const ServiceIcon = SERVICE_ICONS[serviceType] || SERVICE_ICONS.default;
  const colors = SERVICE_COLORS[serviceType] || SERVICE_COLORS.default;
  
  // Countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          onClose?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerRef.current);
  }, [onClose]);

  // Play alert sound
  useEffect(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const ctx = new AudioContext();
      
      // Urgent triple chime
      const playChime = (freq, delay) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.25);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.25);
      };
      
      playChime(880, 0);
      playChime(1100, 0.15);
      playChime(1320, 0.3);
    } catch (e) {
      console.log('Sound failed:', e);
    }
  }, []);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept?.(job);
      toast.success('Job accepted! Check your schedule.');
      onClose?.();
    } catch (error) {
      toast.error(error.message || 'Failed to accept job');
    } finally {
      setAccepting(false);
    }
  };

  const handleSchedule = async () => {
    setScheduling(true);
    try {
      await onSchedule?.(job);
      toast.success('Job added to your schedule.');
      onClose?.();
    } catch (error) {
      toast.error(error.message || 'Failed to schedule job');
    } finally {
      setScheduling(false);
    }
  };

  const handleDecline = () => {
    onDecline?.(job);
    onClose?.();
  };

  const isUrgent = job?.is_asap || job?.scheduled_type === 'asap' || countdown < 30;
  const fare = job?.estimated_fare || job?.total || job?.price || 0;
  const pickupAddress = job?.pickup_location?.address || job?.pickup_address || job?.address?.address || 'Address pending';
  const dropoffAddress = job?.dropoff_location?.address || job?.dropoff_address || '';
  const customerName = job?.customer_name || job?.guest?.name || 'Customer';
  const scheduledTime = job?.scheduled_time || job?.pickup_time || 'ASAP';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className={`w-full max-w-md bg-gradient-to-b ${colors.bg} backdrop-blur-xl rounded-2xl border ${colors.border} shadow-2xl overflow-hidden`}
        >
          {/* Urgent Banner */}
          {isUrgent && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 flex items-center justify-center gap-2">
              <Zap className="w-4 h-4 text-white animate-pulse" />
              <span className="text-white font-semibold text-sm">URGENT - Respond Now!</span>
              <Zap className="w-4 h-4 text-white animate-pulse" />
            </div>
          )}
          
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.bg} border ${colors.border}`}>
                  <ServiceIcon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">New {serviceType.charAt(0).toUpperCase() + serviceType.slice(1)} Job</h2>
                  <p className="text-sm text-white/60">#{job?.reference_number || job?.id?.slice(0, 8)}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Fare */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
              <p className="text-sm text-white/60 mb-1">Estimated Earnings</p>
              <p className="text-4xl font-bold text-[#D4AF37]">${fare.toFixed(2)}</p>
            </div>

            {/* Customer */}
            <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-xl">
              <User className="w-5 h-5 text-[#D4AF37]" />
              <div>
                <p className="text-sm text-white/60">Customer</p>
                <p className="text-white font-medium">{customerName}</p>
              </div>
            </div>

            {/* Locations */}
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-xl">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-emerald-400 font-medium uppercase">Pickup</p>
                  <p className="text-white text-sm truncate">{pickupAddress}</p>
                </div>
              </div>
              
              {dropoffAddress && (
                <div className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-xl">
                  <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-red-400 font-medium uppercase">Dropoff</p>
                    <p className="text-white text-sm truncate">{dropoffAddress}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Time */}
            <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-xl">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              <div>
                <p className="text-sm text-white/60">Pickup Time</p>
                <p className="text-white font-medium">{scheduledTime}</p>
              </div>
            </div>

            {/* Countdown */}
            <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
              <Timer className="w-4 h-4" />
              <span>Auto-dismiss in {countdown}s</span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 border-t border-white/10 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-3"
              >
                {accepting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Accept Now
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleSchedule}
                disabled={scheduling}
                variant="outline"
                className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-semibold py-3"
              >
                {scheduling ? (
                  <div className="w-5 h-5 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
                ) : (
                  <>
                    <Calendar className="w-5 h-5 mr-2" />
                    Schedule
                  </>
                )}
              </Button>
            </div>
            
            <Button
              onClick={handleDecline}
              variant="ghost"
              className="w-full text-white/50 hover:text-white hover:bg-red-500/10"
            >
              <X className="w-4 h-4 mr-2" />
              Decline Job
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NewJobPopup;
